import { cn } from 'src/utils'

export interface JobRailProps {
  /** Drives the "N open positions" heading. */
  positionCount: number
  /** The board's distinct cities, rendered as "Queens, NY · Newark, NJ". */
  locations?: string[]
  searchValue: string
  onSearchChange: (value: string) => void
  searchPlaceholder?: string
  /** The rendered `JobCard`s — the page owns which cards exist and in what order. */
  children: React.ReactNode
  className?: string
}

const IconSearch = () => (
  <svg
    className="h-[15px] w-[15px] flex-none text-doubleNickel-gray-400"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4.3-4.3" />
  </svg>
)

/**
 * The board's left column: how many positions are open and where, a search
 * field, then the cards.
 *
 * The page scrolls and the rail sticks — there is no fixed-height shell and no
 * nested scroll region. The rail only takes a scrollbar of its own when the
 * list outgrows the viewport.
 */
const JobRail = ({
  positionCount,
  locations,
  searchValue,
  onSearchChange,
  searchPlaceholder = 'Search positions',
  children,
  className,
}: JobRailProps) => {
  const locationLine = (locations ?? []).filter(Boolean).join(' · ')

  return (
    <aside
      aria-label="Open positions"
      className={cn(
        // top-6 matches the board container's py-6 so the rail pins exactly
        // where it rests instead of lurching up on the first scroll
        'sticky top-6 flex max-h-[calc(100vh-3rem)] flex-col gap-2.5 overflow-y-auto pr-0.5',
        className
      )}
    >
      <div className="px-0.5 pb-1">
        <h2 className="m-0 text-[18px] font-extrabold leading-tight text-doubleNickel-gray-900">
          {positionCount} open position{positionCount === 1 ? '' : 's'}
        </h2>
        {locationLine && (
          <div
            // Two lines then an ellipsis — a carrier hiring across a dozen
            // cities otherwise pushed the search field and the first card
            // below the fold.
            className="mt-0.5 line-clamp-2 text-[13px] text-doubleNickel-gray-500"
            title={locationLine}
          >
            {locationLine}
          </div>
        )}
        <div
          role="search"
          className="mt-3 flex items-center gap-2 rounded-lg border border-doubleNickel-gray-300 bg-white px-3 py-[9px] shadow-sm focus-within:border-doubleNickel-brand-500"
        >
          <IconSearch />
          <input
            type="search"
            aria-label={searchPlaceholder}
            placeholder={searchPlaceholder}
            value={searchValue}
            onChange={(event) => onSearchChange(event.currentTarget.value)}
            className="w-full min-w-0 border-none bg-transparent p-0 text-sm text-doubleNickel-gray-800 outline-none placeholder:text-doubleNickel-gray-400"
          />
        </div>
      </div>
      {children}
    </aside>
  )
}

export default JobRail
